/**
 * Login Page Controller
 * ======================
 * 
 * Client-side controller for the customer login page at /account/login.
 * 
 * ## Responsibilities
 * - Display prescription banners for users coming from prescription flows
 * - Toggle login/recover password forms based on URL hash (#login, #recover, #resend_email)
 * - Handle recover password form submission with customer state awareness
 * - Handle login form submission with activation email triggers
 * - Manage notification display for password recovery flow
 * 
 * ## Post-Login Error Handling
 * Login form errors (incorrect password, etc.) are handled by inline scripts in
 * `login-customer-form.liquid`. This is because those scripts need to run before
 * `LoginPage.init()` completes (DOMContentLoaded listeners fire in registration order).
 * 
 * ## Architecture
 * This module uses explicit dependency injection - all Shopify/Liquid-dependent values
 * (translations, URLs, shop config) are passed in via the `init()` method. This:
 * - Makes dependencies explicit and traceable
 * - Enables proper IDE support and testing
 * - Separates markup/config (Liquid) from behavior (JS)
 * 
 * ## Dependencies (Global Functions from other assets)
 * These functions must be loaded before this script:
 * 
 * From `customer-custom.js`:
 * - `isEmailValid(email)` - Email format validation
 * - `showError(inputEl, message)` - Display field-level error
 * - `showSuccess(inputEl)` - Clear field-level error state
 * 
 * From `custom.js`:
 * - `showNotice(title, message, linkUrl, linkText)` - Display page-level notice
 * - `fetchCustomerGidAndState(shop, email)` - Get customer state from backend
 * - `handleSendPasswordResetEmail(email)` - Trigger Shopify password reset
 * - `handleSendActivationEmail(email)` - Trigger account activation email
 * 
 * ## Usage
 * ```liquid
 * <script src="{{ 'login-page.js' | asset_url }}" defer></script>
 * <script>
 *   document.addEventListener('DOMContentLoaded', () => {
 *     LoginPage.init({
 *       shop: {{ shop.permanent_domain | json }},
 *       urls: { ... },
 *       translations: { ... },
 *       prescriptionMessages: { ... }
 *     });
 *   });
 * </script>
 * ```
 */

const LoginPage = (function() {
  'use strict';

  // ============================================================================
  // Constants
  // ============================================================================

  /**
   * Customer state values returned by the backend API.
   * Used to determine which action to take (reset password, send activation, etc.)
   */
  const CUSTOMER_STATES = {
    /** Customer was invited but hasn't activated - needs activation email */
    NEEDS_ACTIVATION: ['INVITED', 'DECLINED'],
    /** Customer has an active account - can reset password normally */
    HAS_ACTIVATED_ACCOUNT: ['ENABLED'],
    /** Customer account is disabled - needs to contact support */
    NEEDS_SUPPORT: ['DISABLED'],
    /** No customer found with this email */
    USER_NOT_FOUND: ['NOT_FOUND'],
    /** Backend returned an error or unknown state */
    UNKNOWN: ['UNKNOWN', 'INVALID']
  };

  /**
   * URL hash values that trigger different form states
   */
  const HASH_STATES = {
    RECOVER: ['#recover', '#resend_email'],
    LOGIN: ['#login', '']
  };

  // ============================================================================
  // Private State
  // ============================================================================

  /** Configuration passed from Liquid template */
  let config = null;

  /** Cached DOM element references */
  let elements = {};

  // ============================================================================
  // DOM Utilities
  // ============================================================================

  /**
   * Cache all DOM elements used by the login page.
   * Called once during initialization to avoid repeated DOM queries.
   */
  function cacheElements() {
    elements = {
      // Wrappers
      loginSignupWrapper: document.querySelector('.login-signup-wrapper'),
      signupWrapper: document.querySelector('.signup-wrapper'),
      
      // Headers
      loginHeader: document.getElementById('login-header'),
      forgotPasswordHeader: document.getElementById('forgot-password-header'),
      
      // Forms
      loginForm: document.getElementById('login-customer-form'),
      forgotPasswordForm: document.getElementById('forgot-password-form'),
      
      // Notice elements
      notice: document.getElementById('main-login-notice'),
      noticeTitle: document.getElementById('main-login-notice-title'),
      noticeMessage: document.getElementById('main-login-notice-message'),
      noticeLink: document.getElementById('main-login-notice-link'),
      
      // Buttons
      recoverButton: document.getElementById('RecoverSubmit'),
      loginButton: document.getElementById('LoginSubmit')
    };
  }

  /**
   * Check if the current URL hash indicates the recover password form should show
   * @returns {boolean}
   */
  function isRecoverHash() {
    return HASH_STATES.RECOVER.includes(window.location.hash);
  }

  // ============================================================================
  // Prescription Banner
  // ============================================================================

  /**
   * Display a prescription banner if the user is coming from a prescription flow.
   * Reads state from localStorage and creates a dismissible banner.
   */
  function handlePrescriptionBanner() {
    const prescriptionData = localStorage.getItem('to_prescription');
    const patientStatusData = localStorage.getItem('patient_status');

    if (!prescriptionData || !patientStatusData || !elements.loginSignupWrapper) {
      return;
    }

    try {
      const { value: prescriptionValue } = JSON.parse(prescriptionData);
      const { value: statusValue } = JSON.parse(patientStatusData);

      // Create banner elements
      const banner = document.createElement('div');
      banner.classList.add('login-prescription-banner');

      const closeButton = document.createElement('span');
      closeButton.innerHTML = '&times;';
      closeButton.setAttribute('aria-label', 'Close prescription banner');
      closeButton.style.cursor = 'pointer';
      closeButton.addEventListener('click', () => {
        banner.style.display = 'none';
      });

      const content = document.createElement('p');
      content.innerHTML = statusValue === 'active' 
        ? config.prescriptionMessages.active 
        : config.prescriptionMessages.pending;

      // Assemble and insert banner
      elements.loginSignupWrapper.classList.add('patient-active');
      banner.appendChild(closeButton);
      banner.appendChild(content);
      elements.loginSignupWrapper.prepend(banner);
    } catch (error) {
      console.warn('LoginPage: Failed to parse prescription data', error);
    }
  }

  // ============================================================================
  // Form Visibility Management
  // ============================================================================

  /**
   * Update the signup wrapper visibility based on the current hash.
   * When in recover mode, hide the signup column and make login full width.
   */
  function updateSignupWrapperVisibility() {
    if (!elements.signupWrapper) return;

    if (isRecoverHash()) {
      elements.signupWrapper.style.display = 'none';
      elements.loginSignupWrapper?.classList.add('fullWidth');
    } else {
      elements.signupWrapper.style.display = '';
      elements.loginSignupWrapper?.classList.remove('fullWidth');
    }
  }

  /**
   * Toggle between login and recover password forms based on URL hash.
   * 
   * @param {boolean} preserveNoticesOnLogin - If true, don't clear notices when
   *   showing the login form (used on initial page load when there may be
   *   client-side state to display)
   */
  function toggleForms(preserveNoticesOnLogin = false) {
    const { loginHeader, loginForm, forgotPasswordHeader, forgotPasswordForm } = elements;
    
    if (!forgotPasswordForm || !loginForm) return;

    // Hide all forms initially
    hideElement(forgotPasswordHeader);
    hideElement(forgotPasswordForm);
    hideElement(loginHeader);
    hideElement(loginForm);

    const showRecoverForm = isRecoverHash();

    // Determine if we should preserve existing notices
    const shouldPreserveNotices = !showRecoverForm && preserveNoticesOnLogin && hasClientStateOrNotice();

    if (showRecoverForm) {
      // Switching to recover form - clear any stale state
      sessionStorage.removeItem('customer_state');
      hideNotice();
      
      showElement(forgotPasswordHeader);
      showElement(forgotPasswordForm);
    } else {
      // Switching to login form
      if (!shouldPreserveNotices) {
        sessionStorage.removeItem('customer_state');
        hideNotice();
      }
      
      showElement(loginHeader);
      showElement(loginForm);
    }
  }

  /**
   * Check if there's client-side state or an existing notice to preserve
   * @returns {boolean}
   */
  function hasClientStateOrNotice() {
    const hasClientState = !!sessionStorage.getItem('customer_state');
    const noticeHasContent = elements.noticeMessage && 
      (elements.noticeMessage.textContent.trim() || elements.noticeMessage.innerHTML.trim());
    return hasClientState || noticeHasContent;
  }

  // ============================================================================
  // Notice Management
  // ============================================================================

  /**
   * Hide the notice banner and clear its content
   */
  function hideNotice() {
    if (!elements.notice) return;

    elements.notice.style.display = 'none';
    
    if (elements.noticeMessage) {
      elements.noticeMessage.textContent = '';
      elements.noticeMessage.innerHTML = '';
    }
    if (elements.noticeLink) {
      elements.noticeLink.href = '';
      elements.noticeLink.textContent = '';
    }
  }

  // ============================================================================
  // Form Handlers
  // ============================================================================

  /**
   * Handle the recover password form submission.
   * 
   * Flow:
   * 1. Validate email format
   * 2. Query backend for customer state
   * 3. Based on state:
   *    - ENABLED: Send password reset email
   *    - INVITED/DECLINED: Send activation email
   *    - DISABLED: Show contact support message
   *    - NOT_FOUND: Show create account link
   *    - UNKNOWN: Show generic error
   * 
   * @param {Event} event - Click event from the recover button
   */
  async function handleRecoverPasswordSubmit(event) {
    event.preventDefault();

    const submitBtn = event.currentTarget;
    const form = submitBtn.closest('form');
    
    if (!form) {
      console.warn('LoginPage: Recover password form not found');
      return;
    }

    const emailInput = form.querySelector('input[name="email"]');
    if (!emailInput) {
      console.warn('LoginPage: Email input not found in recover form');
      return;
    }

    const email = emailInput.value.trim();

    // Client-side validation
    if (!validateEmail(emailInput, email)) {
      return;
    }

    // Show loading state
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Sending...';
    submitBtn.disabled = true;

    try {
      const result = await fetchCustomerGidAndState(config.shop, email);
      const customerState = result?.data?.state;

      // Handle network/backend errors
      if (!result || result.error === true) {
        console.error('LoginPage: Recover password error', result);
        showNotice(
          config.translations.errorTitle,
          config.translations.genericError
        );
        return;
      }

      // Branch by customer state
      await handleRecoverByCustomerState(customerState, email);

    } catch (error) {
      console.error('LoginPage: Recover password error', error);
      showNotice(
        config.translations.errorTitle,
        config.translations.genericError
      );
    } finally {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }
  }

  /**
   * Handle the recover flow based on customer state
   * @param {string} customerState - State returned from backend
   * @param {string} email - Customer email address
   */
  async function handleRecoverByCustomerState(customerState, email) {
    const { translations, urls } = config;

    if (CUSTOMER_STATES.HAS_ACTIVATED_ACCOUNT.includes(customerState)) {
      // Customer has an active account - send password reset
      if (typeof handleSendPasswordResetEmail === 'function') {
        const result = await handleSendPasswordResetEmail(email);
        if (result.error) {
          throw new Error(result.message);
        }
      }
      showNotice(null, translations.checkYourInbox);

    } else if (CUSTOMER_STATES.NEEDS_ACTIVATION.includes(customerState)) {
      // Customer needs to activate - send activation email
      showNotice(null, translations.checkYourInbox);
      if (typeof handleSendActivationEmail === 'function') {
        handleSendActivationEmail(email);
      }

    } else if (CUSTOMER_STATES.NEEDS_SUPPORT.includes(customerState)) {
      // Customer account is disabled
      showNotice(
        translations.errorTitle,
        translations.contactSupport,
        urls.customerService,
        translations.contactSupportLinkText
      );

    } else if (CUSTOMER_STATES.USER_NOT_FOUND.includes(customerState)) {
      // No account found
      showNotice(
        null,
        translations.noAccountFound,
        urls.createAccount,
        translations.createAccountLinkText
      );

    } else {
      // Unknown state
      showNotice(
        translations.errorTitle,
        translations.genericError
      );
    }
  }

  /**
   * Handle the login form submission.
   * 
   * Flow:
   * 1. Validate email and password
   * 2. Query backend for customer state
   * 3. If customer needs activation, send activation email before form submit
   * 4. Store state in sessionStorage for post-submit notice display
   * 5. Submit the native Shopify login form
   * 
   * @param {Event} event - Click event from the login button
   */
  async function handleLoginSubmit(event) {
    event.preventDefault();

    const submitBtn = event.currentTarget;
    const form = submitBtn.closest('form');
    
    if (!form) {
      console.warn('LoginPage: Login form not found');
      return;
    }

    const originalText = submitBtn.textContent;
    
    // Helper to restore button state (only called on failure)
    function restoreButton() {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }

    submitBtn.textContent = 'Sending...';
    submitBtn.disabled = true;

    try {
      const emailInput = form.querySelector('input[name="customer[email]"]');
      const passwordInput = form.querySelector('input[name="customer[password]"]');
      const email = emailInput?.value.trim() || '';

      // Validate email
      if (!validateEmail(emailInput, email)) {
        restoreButton();
        return;
      }

      // Validate password
      if (!validatePassword(passwordInput)) {
        restoreButton();
        return;
      }

      // Get customer state from backend
      const result = await fetchCustomerGidAndState(config.shop, email);
      const customerState = result?.data?.state;

      // Store state for post-submit notice display (if not activated)
      if (!CUSTOMER_STATES.HAS_ACTIVATED_ACCOUNT.includes(customerState)) {
        sessionStorage.setItem('customer_state', customerState);
      }

      // Send activation email if needed (before form submit clears email)
      if (CUSTOMER_STATES.NEEDS_ACTIVATION.includes(customerState)) {
        if (typeof handleSendActivationEmail === 'function') {
          handleSendActivationEmail(email);
        }
      }

      // Submit the native Shopify login form
      // NOTE: Don't restore button here - page will reload
      form.submit();

    } catch (error) {
      console.error('LoginPage: Login error', error);
      restoreButton();
    }
  }

  // ============================================================================
  // Validation Helpers
  // ============================================================================

  /**
   * Validate an email input field
   * @param {HTMLInputElement} emailInput - The email input element
   * @param {string} email - The trimmed email value
   * @returns {boolean} True if valid
   */
  function validateEmail(emailInput, email) {
    if (!email) {
      if (emailInput && typeof showError === 'function') {
        showError(emailInput, 'Email cannot be blank.');
      }
      emailInput?.focus();
      return false;
    }

    if (typeof isEmailValid === 'function' && !isEmailValid(email)) {
      if (emailInput && typeof showError === 'function') {
        showError(emailInput, 'Email is not valid.');
      }
      emailInput?.focus();
      return false;
    }

    if (emailInput && typeof showSuccess === 'function') {
      showSuccess(emailInput);
    }
    return true;
  }

  /**
   * Validate a password input field
   * @param {HTMLInputElement} passwordInput - The password input element
   * @returns {boolean} True if valid
   */
  function validatePassword(passwordInput) {
    if (!passwordInput) return true;

    if (!passwordInput.value?.trim()) {
      if (typeof showError === 'function') {
        showError(passwordInput, 'Password cannot be blank.');
      }
      passwordInput.focus();
      return false;
    }

    if (typeof showSuccess === 'function') {
      showSuccess(passwordInput);
    }
    return true;
  }

  // ============================================================================
  // Keyboard Handling
  // ============================================================================

  /**
   * Handle Enter key presses to submit the correct form.
   * When recover form is visible, Enter submits recover.
   * When login form is visible, Enter submits login.
   * 
   * @param {KeyboardEvent} event
   */
  function handleEnterKeySubmit(event) {
    if (event.key !== 'Enter' && event.keyCode !== 13) {
      return;
    }

    const target = event.target;
    if (!target || target.tagName !== 'INPUT') {
      return;
    }

    const isInRecoverForm = elements.forgotPasswordForm?.contains(target);
    const isInLoginForm = elements.loginForm?.contains(target);
    const showingRecoverForm = isRecoverHash();

    if (showingRecoverForm && isInRecoverForm) {
      // Submit recover form
      event.preventDefault();
      event.stopPropagation();
      elements.recoverButton?.click();
    } else if (showingRecoverForm && isInLoginForm) {
      // Prevent hidden login form submission
      event.preventDefault();
      event.stopPropagation();
    } else if (!showingRecoverForm && isInRecoverForm) {
      // Prevent hidden recover form submission
      event.preventDefault();
      event.stopPropagation();
    }
    // Login form Enter key is handled naturally by the click handler
  }

  // ============================================================================
  // DOM Helpers
  // ============================================================================

  function showElement(el) {
    if (el) el.style.display = '';
  }

  function hideElement(el) {
    if (el) el.style.display = 'none';
  }

  // ============================================================================
  // Event Binding
  // ============================================================================

  /**
   * Bind all event listeners
   */
  function bindEvents() {
    // Form submit handlers
    if (elements.recoverButton) {
      elements.recoverButton.addEventListener('click', handleRecoverPasswordSubmit);
    }

    if (elements.loginButton) {
      elements.loginButton.addEventListener('click', handleLoginSubmit);
    }

    // Enter key handler
    document.addEventListener('keydown', handleEnterKeySubmit);

    // Hash change handler
    window.addEventListener('hashchange', () => {
      updateSignupWrapperVisibility();
      toggleForms(false); // Always hide notices on hash change
    });
  }

  // ============================================================================
  // Consultation Flow Handler
  // ============================================================================

  /**
   * Override the login form's return_to if the user is coming from a
   * consultation flow, so they land back on the product page they started from
   * rather than on the default post-login destination.
   */
  function handleConsultationReturnUrl() {
    const form = document.getElementById('customer_login');
    if (!form) return;

    try {
      const productData = JSON.parse(localStorage.getItem('consultation_product'));
      if (productData?.url && productData.url.trim()) {
        // Set return_to in exactly one place. Shopify accepts it from either the
        // form body or the action query string, and submitting both leaves the
        // winner undocumented -- so the consultation override and the {% form %}
        // tag's own default would race with no defined outcome.
        const returnTo = form.querySelector('input[name="return_to"]');
        const target = `${productData.url}#product-details`;

        if (returnTo) {
          // Preferred: overwrite the hidden field the form tag emits.
          returnTo.value = target;
        } else {
          // Fallback for a form that carries return_to in its action instead.
          // Only reached when the hidden input is absent, so this still leaves
          // return_to submitted exactly once.
          const actionUrl = new URL(form.action, window.location.origin);
          actionUrl.searchParams.set('return_to', target);
          form.action = actionUrl.toString();
        }
      }
    } catch (error) {
      console.warn('LoginPage: Failed to parse consultation product data', error);
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================

  return {
    /**
     * Customer state constants - exposed for use by login form snippet
     */
    CUSTOMER_STATES,

    /**
     * Initialize the login page controller.
     * 
     * @param {Object} cfg - Configuration object
     * @param {string} cfg.shop - Shop permanent domain (e.g., 'myshop.myshopify.com')
     * @param {Object} cfg.urls - URL configuration
     * @param {string} cfg.urls.customerService - Customer service page URL
     * @param {string} cfg.urls.createAccount - Account registration URL
     * @param {string} cfg.urls.forgotPassword - Forgot password URL
     * @param {Object} cfg.translations - Translated strings for password recovery flow
     * @param {string} cfg.translations.errorTitle - Error notice title
     * @param {string} cfg.translations.genericError - Generic error message
     * @param {string} cfg.translations.checkYourInbox - Success message after sending email
     * @param {string} cfg.translations.contactSupport - Message to contact support
     * @param {string} cfg.translations.contactSupportLinkText - Link text for support
     * @param {string} cfg.translations.noAccountFound - Message when no account exists
     * @param {string} cfg.translations.createAccountLinkText - Link text for create account
     * @param {Object} cfg.prescriptionMessages - Prescription banner messages
     * @param {string} cfg.prescriptionMessages.active - Message for active patients
     * @param {string} cfg.prescriptionMessages.pending - Message for pending patients
     */
    init(cfg) {
      config = cfg;
      
      cacheElements();
      bindEvents();
      handlePrescriptionBanner();
      
      // Initial form state
      updateSignupWrapperVisibility();
      
      // Delay form toggle slightly to ensure DOM is fully ready
      // and any inline scripts have populated notices
      setTimeout(() => {
        toggleForms(true); // Preserve notices on initial load
      }, 0);

      // Handle consultation return URL
      handleConsultationReturnUrl();
    },

    /**
     * Hide the notice banner (exposed for external use)
     */
    hideNotice
  };
})();

// Expose globally for backward compatibility
window.LoginPage = LoginPage;
